// Display mirror of backend/src/lib/brand.ts — the Abacas look for anything a
// page renders for download (today: the gap-analysis PDF in ExecutiveReport.tsx;
// every other document is rendered by the API).
//
// Same resolution the server uses: the organisation's brand colours when it set
// them in My Organisation, the Abacas palette otherwise. A page cannot apply an
// uploaded Word template — that merge only happens server-side — so anything
// that must follow a client template is generated by the API, not here.
//
// Keep the hex values in step with backend/src/lib/brand.ts.

export const ABACAS_PRINT = {
  heading: "0F0520",
  accent: "9B00D9",
  subheading: "3B1D63",
  text: "1E0A3C",
  muted: "6B4D9A",
  rule: "E4D8F7",
  tile: "F3EEFF",
  zebra: "FAF7FF",
} as const;

export type Rgb = [number, number, number];

export function toRgb(hex: string): Rgb {
  const h = hex.replace(/^#/, "");
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as Rgb;
}

/** Mixes a colour towards white by `amount` (0–1) — for tints of an accent. */
export function tint(hex: string, amount: number): Rgb {
  return toRgb(hex).map((v) => Math.round(v + (255 - v) * amount)) as Rgb;
}

export interface ExportBrandSource {
  primaryBrandColor?: string | null;
  accentBrandColor?: string | null;
  useBrandColorsInPolicies?: boolean | null;
}

export interface ExportPalette {
  heading: Rgb;
  accent: Rgb;
  text: Rgb;
  muted: Rgb;
  rule: Rgb;
  tile: Rgb;
  /** True when the organisation's own colours are in use rather than the Abacas palette. */
  branded: boolean;
}

const valid = (v: string | null | undefined) => (/^#?[0-9A-Fa-f]{6}$/.test(v ?? "") ? v!.replace(/^#/, "") : null);

export function resolveExportPalette(org: ExportBrandSource | null | undefined): ExportPalette {
  const on = !!org?.useBrandColorsInPolicies;
  const heading = (on && valid(org?.primaryBrandColor)) || ABACAS_PRINT.heading;
  const accent = (on && valid(org?.accentBrandColor)) || ABACAS_PRINT.accent;
  return {
    heading: toRgb(heading),
    accent: toRgb(accent),
    text: toRgb(ABACAS_PRINT.text),
    muted: toRgb(ABACAS_PRINT.muted),
    rule: tint(accent, 0.72),
    tile: tint(accent, 0.9),
    branded: on && (!!valid(org?.primaryBrandColor) || !!valid(org?.accentBrandColor)),
  };
}
